/**
 * Orders tab — every real IBKR order (NettedOrder) as a sortable data table.
 *
 * RAW view: a sortable table (click any header) with a Latest-day / All toggle
 * (defaults to the most recent day's orders; older orders behind "All"). Expand a
 * row for the staged/failure note + contributing strategies. Actions per row —
 * Release / Remove (staged), Cancel (open), Retry (rejected/expired) — all behind
 * a confirm dialog. NETTED view: a view-only dry-run of release-time netting.
 *
 * Mobile-first: the table scrolls horizontally on small screens. Theme-aware
 * (gold accent in dark, cobalt in light); status chips use semantic colours.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { tradingService } from '../../services/tradingService';
import useSharedAccount, { readSharedAccountId } from './portfolio/useSharedAccount';

// ─── utilities ───────────────────────────────────────────────────────────────
const fmtDateTime = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return s; } };
const fmtTime = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
const fmtMoney = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`);
const dayKey = (s) => { if (!s) return ''; try { return new Date(s).toLocaleDateString('en-CA'); } catch { return ''; } };

const STATUS_COLOR = {
  staged: 'amber', placed: 'blue', partial: 'amber', filled: 'green',
  rejected: 'red', cancelled: 'gray', expired: 'gray',
};

const Pill = ({ children, color = 'gray' }) => {
  const c = {
    gold: 'text-brand-gold bg-brand-gold/10 border-brand-gold/30',
    green: 'text-green-500 bg-green-500/12 border-green-500/30',
    red: 'text-red-500 bg-red-500/12 border-red-500/30',
    blue: 'text-blue-500 bg-blue-500/12 border-blue-500/30',
    amber: 'text-amber-500 bg-amber-500/12 border-amber-500/30',
    gray: 'text-gray-500 bg-gray-500/12 border-gray-500/25',
  }[color] || 'text-gray-500 bg-gray-500/12 border-gray-500/25';
  return <span className={`inline-block px-1.5 py-0.5 text-[9px] rounded font-mono uppercase tracking-wider border ${c}`}>{children}</span>;
};

// ─── minimal toast ───────────────────────────────────────────────────────────
const useToast = () => {
  const [msg, setMsg] = useState(null);
  const show = useCallback((m, isErr = false) => { setMsg({ m, isErr }); setTimeout(() => setMsg(null), 4000); }, []);
  const node = msg ? (
    <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded shadow-lg text-sm ${msg.isErr ? 'bg-red-700 text-red-50' : 'bg-gray-800 text-gray-100'}`}>{msg.m}</div>
  ) : null;
  return [show, node];
};

// ─── confirm dialog ──────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, title, body, danger, busy, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
        <h3 className="font-serif text-lg text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
        <div className="text-sm text-gray-600 dark:text-gray-300 mb-5">{body}</div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">Back</button>
          <button onClick={onConfirm} disabled={busy} className={`px-3 py-1.5 text-sm rounded text-white disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-500' : 'bg-brand-gold hover:opacity-90'}`}>{busy ? 'Working…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── expandable per-order detail (notes + contributing strategies) ─────────────
const OrderDetail = ({ order }) => {
  const failed = order.status === 'rejected' || order.status === 'expired';
  return (
    <div className="space-y-2">
      {order._netted && (
        order._cross ? (
          <div className="text-xs text-gray-400 bg-gray-500/5 border border-gray-600/30 rounded px-2 py-1.5"><span className="uppercase tracking-wider text-[10px] text-gray-500">Internal cross</span> — the {order._sourceCount} staged orders net to zero, so nothing reaches IBKR.</div>
        ) : (
          <div className="text-xs text-amber-500 bg-amber-500/8 border border-amber-500/30 rounded px-2 py-1.5"><span className="uppercase tracking-wider text-[10px]">Will place</span> — folds {order._sourceCount} staged order{order._sourceCount === 1 ? '' : 's'} into one order at release. Use “Release all staged” to send.</div>
        )
      )}
      {order.status === 'staged' && order.error_message && /offline/i.test(order.error_message) ? (
        <div className="text-xs text-amber-500 bg-amber-500/8 border border-amber-500/30 rounded px-2 py-1.5">
          <span className="uppercase tracking-wider text-[10px]">Held — IBKR offline:</span>{' '}{order.error_message} Relogin under IBKR Accounts, then press “Release now”.
        </div>
      ) : order.status === 'staged' && (
        <div className="text-xs text-amber-500 bg-amber-500/8 border border-amber-500/30 rounded px-2 py-1.5">
          <span className="uppercase tracking-wider text-[10px]">Staged</span>{' '}— held for pre-open review. Target release: <span className="font-mono">{fmtDateTime(order.release_at)}</span>.
        </div>
      )}
      {failed && order.error_message && (
        <div className="text-xs text-red-500 bg-red-500/8 border border-red-500/30 rounded px-2 py-1.5"><span className="uppercase tracking-wider text-[10px]">Reason:</span> {order.error_message}</div>
      )}
      {failed && order.was_retried && (
        <div className="text-xs text-green-500 bg-green-500/8 border border-green-500/30 rounded px-2 py-1.5"><span className="uppercase tracking-wider text-[10px]">Handled:</span> already retried — a newer order took this position. Retry disabled to prevent a double fill.</div>
      )}
      {order.ibkr_order_id && <div className="text-[11px] text-gray-500 font-mono">IBKR order #{order.ibkr_order_id}</div>}
      {/* Netted rows: show the individual staged orders that fold into this one. */}
      {order._netted && order._sources && order._sources.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1.5">{order._sources.length} contributing order{order._sources.length === 1 ? '' : 's'}</div>
          <ul className="space-y-1.5">
            {order._sources.map((s) => (
              <li key={s.id} className="text-xs border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                <span className={s.action === 'BUY' ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>{s.action} {s.quantity}</span>
                <span className="text-gray-400"> · {s.tif}</span>
                {s.contributing && s.contributing.length > 0 && (
                  <span className="text-gray-500 dark:text-gray-400"> — {s.contributing.map((c) => c.strategy_name).join(', ')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Strategy-level breakdown — for real orders, or netted rows lacking source detail. */}
      {(!order._netted || !(order._sources && order._sources.length)) && order.contributing && order.contributing.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{order.contributing.length} contributing {order.contributing.length === 1 ? 'strategy' : 'strategies'}</div>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
            {order.contributing.map((c) => (
              <li key={c.strategy_id}>{c.strategy_name}: <span className={c.intent_delta >= 0 ? 'text-green-500' : 'text-red-500'}>{c.intent_delta >= 0 ? '+' : ''}{c.intent_delta}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── sortable orders table ─────────────────────────────────────────────────────
const COLS = [
  { key: 'symbol', label: 'Symbol', align: 'left' },
  { key: 'side', label: 'Side', align: 'left' },
  { key: 'qty', label: 'Qty', align: 'right' },
  { key: 'status', label: 'Status', align: 'left' },
  { key: 'account', label: 'Account', align: 'left' },
  { key: 'submitted', label: 'Submitted', align: 'right' },
  { key: 'fill', label: 'Fill', align: 'right' },
  { key: 'filled', label: 'Filled', align: 'right' },
  { key: 'comm', label: 'Comm', align: 'right' },
];
const sortVal = (o, k) => ({
  symbol: o.symbol || '', side: o.action || '', qty: o.quantity || 0, status: o.status || '',
  account: o.account_name || '', submitted: new Date(o.submitted_at || 0).getTime(),
  fill: o.fill_price ?? -1, filled: o.filled_qty ?? 0, comm: o.commission ?? -1,
}[k]);

const OrdersTable = ({ orders, pinned = [], onAct, defaultSort = { key: 'submitted', dir: 'desc' } }) => {
  const [sort, setSort] = useState(defaultSort);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const sortRows = useCallback((arr) => [...arr].sort((a, b) => {
    const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
    const c = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return sort.dir === 'asc' ? c : -c;
  }), [sort]);

  const rows = useMemo(() => sortRows(orders), [orders, sortRows]);
  const pinnedRows = useMemo(() => sortRows(pinned), [pinned, sortRows]);

  const setCol = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }));
  const arrow = (k) => (sort.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  const renderRow = (o) => {
    const failed = o.status === 'rejected' || o.status === 'expired';
    const isOpen = expanded.has(o.id);
    const pct = o.quantity ? Math.round(((o.filled_qty ?? 0) / o.quantity) * 100) : 0;
    const hasDetail = o._netted || o.status === 'staged' || failed || (o.contributing && o.contributing.length) || o.ibkr_order_id;
    return (
      <React.Fragment key={o.id}>
        <tr className={`border-b border-gray-100 dark:border-gray-800/60 hover:bg-gray-50 dark:hover:bg-[#161b22] ${failed ? 'bg-red-500/[0.04]' : ''} ${o._netted ? 'bg-brand-gold/[0.04]' : ''}`}>
          <td className="pl-2">
            {hasDetail ? (
              <button onClick={() => toggle(o.id)} className={`text-gray-400 text-[10px] transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</button>
            ) : null}
          </td>
          <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-gray-100">{o.symbol}</td>
          <td className="py-2.5 px-3">
            {o.action ? (
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${o.action === 'BUY' ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={o.action === 'BUY' ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>{o.action}</span>
              </span>
            ) : <span className="text-gray-400">—</span>}
          </td>
          <td className="py-2.5 px-3 text-right tabular-nums">{o.quantity}</td>
          <td className="py-2.5 px-3 whitespace-nowrap">
            {o._netted
              ? <Pill color={o._cross ? 'gray' : 'amber'}>{o._cross ? 'cross' : 'will place'}</Pill>
              : <Pill color={STATUS_COLOR[o.status] || 'gray'}>{o.status}</Pill>}
            {' '}<Pill color="gray">{o.tif}</Pill>
            {o._netted && <span className="ml-1 text-[10px] text-gray-400">· folds {o._sourceCount}</span>}
          </td>
          <td className="py-2.5 px-3">
            <div className="text-gray-700 dark:text-gray-300">{o.account_name}</div>
            <div className="text-[10px] text-gray-400">{o.account_is_paper ? 'paper' : 'live'}</div>
          </td>
          <td className="py-2.5 px-3 text-right tabular-nums text-gray-500 whitespace-nowrap">{o._netted ? <span className="text-amber-500">pending</span> : fmtTime(o.submitted_at)}</td>
          <td className="py-2.5 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{o._netted ? '—' : fmtMoney(o.fill_price)}</td>
          <td className="py-2.5 px-3 text-right">
            {o._netted ? <span className="text-gray-400">—</span> : (
              <div className="inline-flex items-center gap-2">
                <span className="tabular-nums text-gray-700 dark:text-gray-300">{o.filled_qty ?? 0}/{o.quantity}</span>
                <span className="w-8 h-[5px] rounded-full bg-gray-200 dark:bg-[#20272f] overflow-hidden"><span className="block h-full rounded-full bg-brand-gold" style={{ width: `${pct}%` }} /></span>
              </div>
            )}
          </td>
          <td className="py-2.5 px-3 text-right tabular-nums text-gray-500">{o._netted ? '—' : fmtMoney(o.commission)}</td>
          <td className="py-2.5 px-3">
            {o._netted ? (
              <div className="text-right text-[10px] text-gray-400">{o._cross ? '' : 'via Release all ↑'}</div>
            ) : (
              <div className="flex items-center gap-1.5 justify-end">
                {o.can_release && <button onClick={() => onAct('release', o)} className="px-2 py-1 text-[11px] rounded border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10">Release</button>}
                {o.can_void && <button onClick={() => onAct('void', o)} className="px-2 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">Remove</button>}
                {o.can_cancel && <button onClick={() => onAct('cancel', o)} className="px-2 py-1 text-[11px] rounded border border-red-500/40 text-red-500 hover:bg-red-500/10">Cancel</button>}
                {o.can_retry && <button onClick={() => onAct('retry', o)} className="px-2 py-1 text-[11px] rounded border border-amber-500/40 text-amber-500 hover:bg-amber-500/10">Retry</button>}
              </div>
            )}
          </td>
        </tr>
        {isOpen && hasDetail && (
          <tr className="bg-gray-50/60 dark:bg-[#0d1117]/60">
            <td />
            <td colSpan={COLS.length + 1} className="px-3 py-3"><OrderDetail order={o} /></td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="overflow-x-auto scrollbar-thin rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-[12.5px]" style={{ minWidth: 860 }}>
        <thead>
          <tr className="bg-gray-50 dark:bg-[#12151c] border-b border-gray-200 dark:border-gray-800">
            <th className="w-6" />
            {COLS.map((c) => (
              <th key={c.key} onClick={() => setCol(c.key)}
                className={`font-mono text-[9px] tracking-[0.1em] uppercase text-gray-400 font-normal py-2.5 px-3 cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-200 ${c.align === 'right' ? 'text-right' : 'text-left'} ${sort.key === c.key ? 'text-brand-gold' : ''}`}>
                {c.label}{arrow(c.key)}
              </th>
            ))}
            <th className="py-2.5 px-3 text-right font-mono text-[9px] tracking-[0.1em] uppercase text-gray-400 font-normal">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pinnedRows.length > 0 && (
            <tr className="bg-amber-500/[0.06] border-b border-amber-500/20">
              <td colSpan={COLS.length + 2} className="px-3 py-1.5 text-[9px] font-mono uppercase tracking-[0.12em] text-amber-500">Will place at release · {pinnedRows.length} netted</td>
            </tr>
          )}
          {pinnedRows.map(renderRow)}
          {pinnedRows.length > 0 && rows.length > 0 && (
            <tr className="bg-gray-50 dark:bg-[#12151c] border-b border-gray-200 dark:border-gray-800">
              <td colSpan={COLS.length + 2} className="px-3 py-1.5 text-[9px] font-mono uppercase tracking-[0.12em] text-gray-400">Placed &amp; historical</td>
            </tr>
          )}
          {rows.map(renderRow)}
        </tbody>
      </table>
    </div>
  );
};

// Map a netted-preview group (what release-time netting will actually place for
// a (account, symbol)) into a synthetic table row the OrdersTable can render.
// `sources` are the individual staged orders that fold into this netted order.
const nettedToRow = (r, sources = []) => ({
  id: `net-${r.account_id}-${r.symbol}`,
  _netted: true,
  _cross: !!r.net_zero,
  _sourceCount: r.source_count,
  _sources: sources,
  symbol: r.symbol,
  action: r.net_zero ? null : r.action,
  quantity: r.net_zero ? 0 : r.quantity,
  tif: r.tif,
  account: r.account_id,
  account_name: r.account_name,
  account_is_paper: r.account_is_paper,
  contributing: (r.contributing || []).map((c) => ({ ...c, intent_delta: Number(c.intent_delta) })),
});

// ─── page ────────────────────────────────────────────────────────────────────
const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Account filter defaults to the account picked in Overview (shared across the
  // Portfolio pillar) and live-follows changes there.
  const [sharedAccountId, setSharedAccountId] = useSharedAccount();
  const [filters, setFilters] = useState(() => {
    const a = readSharedAccountId();
    return { status: '', account: a != null ? String(a) : '', symbol: '' };
  });
  useEffect(() => {
    const want = sharedAccountId == null ? '' : String(sharedAccountId);
    setFilters((f) => (f.account === want ? f : { ...f, account: want }));
  }, [sharedAccountId]);
  // 'summary' = staged folded to what-will-place (netted) + non-staged as-is;
  // 'raw' = every individual order.
  const [viewMode, setViewMode] = useState('summary');
  const [dayView, setDayView] = useState('latest'); // 'latest' | 'all'
  const [showCrosses, setShowCrosses] = useState(false); // net-zero crosses hidden by default
  const [netted, setNetted] = useState({ results: [], raw_count: 0, netted_count: 0 });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showToast, toastNode] = useToast();
  const [dismissedRejectKey, setDismissedRejectKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // Always need the real orders (non-staged rows, staged-release groups,
      // rejected alert). Summary additionally fetches the netting preview.
      const ordersP = tradingService.listOrders({ ...filters, limit: 100 });
      if (viewMode === 'summary') {
        const [ordRes, netRes] = await Promise.all([
          ordersP,
          tradingService.previewNettedOrders({ account: filters.account, symbol: filters.symbol }),
        ]);
        setOrders(ordRes.data.results || []);
        setNetted({ results: netRes.data.results || [], raw_count: netRes.data.raw_count || 0, netted_count: netRes.data.netted_count || 0 });
      } else {
        const { data } = await ordersP;
        setOrders(data.results || []);
      }
    } catch (e) { setErr('Failed to load orders'); } finally { setLoading(false); }
  }, [filters, viewMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { tradingService.listIBKRAccounts().then(({ data }) => setAccounts(Array.isArray(data) ? data : (data?.results || []))).catch(() => {}); }, []);

  const doAction = async () => {
    if (!confirm) return; setBusy(true);
    try {
      const { action, order } = confirm;
      if (action === 'cancel') { await tradingService.cancelOrder(order.id); showToast(`Cancelled ${order.symbol} order`); }
      else if (action === 'release') { const { data } = await tradingService.releaseOrder(order.id); showToast(`Released ${order.symbol} → ${data.status}`, data.status === 'rejected'); }
      else if (action === 'void') { await tradingService.voidOrder(order.id); showToast(`Removed ${order.symbol} from queue`); }
      else if (action === 'release-all') {
        const { data } = await tradingService.releaseAllOrders(order.account);
        if (data?.status === 'release_started') showToast(`Bulk release started for ${data.count} order${data.count === 1 ? '' : 's'}`);
        else { const n = data?.summary?.released ?? 0; showToast(`Released ${n} staged order${n === 1 ? '' : 's'}`); }
      } else { const { data } = await tradingService.retryOrder(order.id); showToast(`Retried ${order.symbol} → ${data.status}`, data.status === 'rejected'); }
      setConfirm(null); await load();
    } catch (e) { showToast(e?.response?.data?.error || 'Action failed', true); setConfirm(null); } finally { setBusy(false); }
  };

  // MODEL (External Book) accounts have no broker — their staged orders are DOWNLOADED as a
  // Bloomberg xlsx (which marks them placed), never released to IBKR. No confirm modal needed.
  const exportModelOrders = async (g) => {
    setBusy(true);
    try {
      const resp = await tradingService.downloadModelOrders(g.account, true);
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `model_orders_${g.account_name || g.account}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      showToast(`Exported ${g.count} staged order${g.count === 1 ? '' : 's'} — now marked placed`);
      await load();
    } catch (e) { showToast(e?.response?.data?.error || 'Export failed', true); } finally { setBusy(false); }
  };

  const sel = 'bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-sm text-gray-800 dark:text-gray-200';

  // staged bulk-release groups (per account) — over ALL loaded orders.
  const stagedGroups = useMemo(() => {
    const g = {};
    orders.forEach((o) => { if (o.status !== 'staged') return; if (!g[o.account]) g[o.account] = { account: o.account, account_name: o.account_name, account_is_paper: o.account_is_paper, account_is_model: o.account_is_model, count: 0 }; g[o.account].count += 1; });
    return Object.values(g);
  }, [orders]);

  const rejectedOrders = useMemo(() => orders.filter((o) => o.status === 'rejected' && !o.was_retried), [orders]);
  const rejectKey = rejectedOrders.map((o) => o.id).sort().join(',');
  const showRejectAlert = rejectedOrders.length > 0 && dismissedRejectKey !== rejectKey;

  // Netted "will place" rows (summary only) — pinned above the historical table.
  // Attach each group's underlying staged orders so the expand shows them.
  const nettedRows = useMemo(() => {
    const stagedByKey = {};
    orders.forEach((o) => {
      if (o.status !== 'staged') return;
      const k = `${o.account}-${o.symbol}`;
      if (!stagedByKey[k]) stagedByKey[k] = [];
      stagedByKey[k].push(o);
    });
    return netted.results.map((r) => nettedToRow(r, stagedByKey[`${r.account_id}-${r.symbol}`] || []));
  }, [netted, orders]);
  const crossCount = useMemo(() => nettedRows.filter((r) => r._cross).length, [nettedRows]);
  const visibleNetted = useMemo(() => (showCrosses ? nettedRows : nettedRows.filter((r) => !r._cross)), [nettedRows, showCrosses]);

  // The sortable/day-filtered body: non-staged orders in summary (staged are
  // folded into nettedRows), every order in raw.
  const bodyOrders = useMemo(
    () => (viewMode === 'summary' ? orders.filter((o) => o.status !== 'staged') : orders),
    [orders, viewMode],
  );
  // Latest-day filter (default). latestDay = most recent submitted local date.
  const latestDay = useMemo(() => bodyOrders.reduce((m, o) => { const d = dayKey(o.submitted_at); return d > m ? d : m; }, ''), [bodyOrders]);
  const displayed = useMemo(
    () => (dayView === 'latest' && latestDay ? bodyOrders.filter((o) => dayKey(o.submitted_at) === latestDay) : bodyOrders),
    [bodyOrders, dayView, latestDay],
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Orders</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm">
            {[['summary', 'Netted'], ['raw', 'Raw']].map(([m, label]) => (
              <button key={m} onClick={() => setViewMode(m)} className={`px-3 py-1.5 ${viewMode === m ? 'bg-brand-gold text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>{label}</button>
            ))}
          </div>
          <button onClick={load} disabled={loading} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">{loading ? 'Refreshing…' : '↻ Refresh'}</button>
        </div>
      </div>

      {showRejectAlert && (
        <div className="mb-3 rounded-lg border border-red-500/60 ring-1 ring-red-500/30 bg-red-50 dark:bg-red-900/15 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1"><span className="text-red-500 text-lg leading-none">⚠</span><span className="font-semibold text-red-600 dark:text-red-300 uppercase tracking-wide text-sm">{rejectedOrders.length} order{rejectedOrders.length === 1 ? '' : 's'} rejected</span></div>
              <ul className="text-xs text-red-600 dark:text-red-300 space-y-0.5">
                {rejectedOrders.map((o) => (
                  <li key={o.id}><span className="font-semibold">{o.symbol} {o.action} {o.quantity}</span> ({o.account_name}){o.error_message ? <> — {o.error_message}</> : null}{o.can_retry ? <span className="ml-1 opacity-80">· use Retry</span> : null}</li>
                ))}
              </ul>
            </div>
            <button onClick={() => setDismissedRejectKey(rejectKey)} className="shrink-0 px-2 py-1 text-xs rounded border border-red-500/50 text-red-500 hover:bg-red-500/10">Dismiss</button>
          </div>
        </div>
      )}

      {viewMode === 'summary' && !err && (
        <div className="mb-3 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>Staged orders shown as <span className="text-amber-500">what will place</span> (netted per symbol at release). Placed &amp; historical shown as-is. Switch to <span className="font-medium">Raw</span> for every individual order.</span>
          {crossCount > 0 && (
            <button onClick={() => setShowCrosses((s) => !s)} className="shrink-0 text-brand-gold hover:underline">
              {showCrosses ? `Hide ${crossCount} crossed (net 0)` : `Show ${crossCount} crossed (net 0)`}
            </button>
          )}
        </div>
      )}

      {/* filters + day toggle */}
      <div className="flex gap-2 items-center overflow-x-auto pb-2 mb-3 scrollbar-thin">
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm mr-1 shrink-0">
          {[['latest', 'Latest day'], ['all', 'All']].map(([id, label]) => (
            <button key={id} onClick={() => setDayView(id)} className={`px-3 py-1 ${dayView === id ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>{label}</button>
          ))}
        </div>
        <select className={sel} value={filters.status} disabled={viewMode === 'summary'} title={viewMode === 'summary' ? 'Status filter available in Raw view' : ''} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          {['staged', 'placed', 'partial', 'filled', 'rejected', 'cancelled', 'expired'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={sel} value={filters.account} onChange={(e) => { const v = e.target.value; setFilters({ ...filters, account: v }); if (v) setSharedAccountId(Number(v)); }}>
          <option value="">All accounts</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.is_paper ? ' (paper)' : ' (live)'}</option>)}
        </select>
        <input className={sel} placeholder="Symbol" value={filters.symbol} onChange={(e) => setFilters({ ...filters, symbol: e.target.value.toUpperCase() })} />
      </div>

      {stagedGroups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {stagedGroups.map((g) => (
            g.account_is_model ? (
              <button key={g.account} onClick={() => exportModelOrders(g)} disabled={busy}
                className="px-3 py-1.5 text-sm rounded-lg border border-emerald-500/40 bg-emerald-500/5 text-emerald-500 hover:bg-emerald-500/10 font-medium disabled:opacity-50">
                Export orders (Bloomberg) · {g.account_name} ({g.count})
              </button>
            ) : (
              <button key={g.account} onClick={() => setConfirm({ action: 'release-all', order: g })}
                className="px-3 py-1.5 text-sm rounded-lg border border-brand-gold/40 bg-brand-gold/5 text-brand-gold hover:bg-brand-gold/10 font-medium">
                Release all staged · {g.account_name} ({g.count})
              </button>
            )
          ))}
        </div>
      )}

      {err && <div className="text-sm text-red-500 mb-2">{err}</div>}

      {!loading && displayed.length === 0 && visibleNetted.length === 0 && !err ? (
        <div className="text-sm text-gray-500 py-8 text-center">
          {crossCount > 0 && viewMode === 'summary' ? 'All staged orders net to zero — nothing will place.'
            : bodyOrders.length && dayView === 'latest' ? 'Nothing on the latest day — switch to “All”.'
            : 'No orders match these filters.'}
        </div>
      ) : (
        <OrdersTable orders={displayed} pinned={viewMode === 'summary' ? visibleNetted : []} defaultSort={{ key: 'qty', dir: 'desc' }} onAct={(action, order) => setConfirm({ action, order })} />
      )}
      {dayView === 'latest' && latestDay && bodyOrders.length > displayed.length && (
        <div className="mt-2 text-[11px] text-gray-400 text-center">Showing {displayed.length} from {latestDay} · {bodyOrders.length - displayed.length} older hidden — “All” to view.</div>
      )}

      <ConfirmDialog
        open={!!confirm} busy={busy} danger={confirm?.action === 'cancel'}
        title={confirm?.action === 'cancel' ? 'Cancel order?' : confirm?.action === 'release' ? 'Release order to IBKR?' : confirm?.action === 'release-all' ? 'Release all staged orders?' : confirm?.action === 'void' ? 'Remove from queue?' : 'Retry order?'}
        body={confirm && (
          <>
            {confirm.action === 'cancel' ? <>This cancels IBKR order #{confirm.order.ibkr_order_id} — {confirm.order.action} {confirm.order.quantity} {confirm.order.symbol}.</>
              : confirm.action === 'release' ? <>This sends the staged order to IBKR now — {confirm.order.action} {confirm.order.quantity} {confirm.order.symbol} ({confirm.order.tif}).</>
              : confirm.action === 'release-all' ? <>This sends <span className="font-semibold">all staged orders</span> for {confirm.order.account_name} to IBKR now.</>
              : confirm.action === 'void' ? <>This removes the queued {confirm.order.action} {confirm.order.quantity} {confirm.order.symbol} order before release. It never goes to IBKR.</>
              : <>This resubmits {confirm.order.action} {confirm.order.quantity} {confirm.order.symbol} as a new order.</>}
            {!confirm.order.account_is_paper && <div className="mt-2 text-red-500 font-semibold">⚠ This is a LIVE account ({confirm.order.account_name}).</div>}
          </>
        )}
        onConfirm={doAction} onCancel={() => setConfirm(null)}
      />
      {toastNode}
    </div>
  );
};

export default OrdersPage;
